import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getDisplayName } from "@/lib/utils/display-name";
import { User, Building2, Handshake, Search, X, Plus } from "lucide-react";
import { people, companies, deals } from "@/data/dummy";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import CompanyLogo from "@/components/shared/CompanyLogo";
import UserAvatar from "@/components/shared/Avatar";
import {
  useNotesSubjectSearchLazyQuery,
  Account,
  Contact,
  Deal,
  Engagement,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  NoteTypeEnum,
} from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { useSettleCreatedNote } from "@/lib/hooks/use-settle-created-note";
import { isLovablePreview } from "@/lib/utils/environment";
import { toast } from "sonner";
import type { PartialBlock } from "@blocknote/core";
import NoteEditor from "@/components/shared/NoteEditor";
import { useFormatters } from "@/lib/i18n/useFormatters";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTitle?: string;
  initialAssociations?: NoteSubject[];
  engagementId?: string; // Pre-populated engagement ID from meeting context
  noteId?: string; // If provided, we're editing an existing note
  initialBlocks?: string; // JSON string of initial blocks for editing
}

export type NoteSubject = Account | Contact | Deal;
type InternalSubject = NoteSubject | Engagement;

export default function AddNoteDialog({ open, onOpenChange, initialTitle, initialAssociations, engagementId, noteId, initialBlocks }: Props) {
  const { t } = useTranslation();
  const { formatDate } = useFormatters();
  const workspaceId = useWorkspaceId();
  const { currentUser } = useCurrentUser();
  const [title, setTitle] = useState(initialTitle || "");
  const [blocks, setBlocks] = useState<PartialBlock[] | undefined>();
  const [subjects, setSubjects] = useState<NoteSubject[]>(initialAssociations || []);
  const [searchValue, setSearchValue] = useState("");
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  // Use engagementId prop directly instead of state, so it updates when prop changes
  const contextEngagementId = engagementId;
  const isEditMode = !!noteId;

  const [createNoteMutation, { loading: creatingNote }] = useCreateNoteMutation();
  const settleCreatedNote = useSettleCreatedNote();
  const [updateNoteMutation, { loading: updatingNote }] = useUpdateNoteMutation();
  const [searchQuery, { data: searchData, loading: searchLoading }] = useNotesSubjectSearchLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounced search
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (searchValue.trim() && workspaceId) {
      debounceTimerRef.current = setTimeout(() => {
        searchQuery({
          variables: {
            workspaceId,
            search: searchValue.trim(),
            first: 10,
          },
        });
      }, 300);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchValue, workspaceId, searchQuery]);

  useEffect(() => {
    if (open) {
      setTitle(initialTitle || "");
      setSubjects(initialAssociations || []);
      // Parse initial blocks if provided (for edit mode)
      if (initialBlocks) {
        try {
          setBlocks(JSON.parse(initialBlocks));
        } catch (error) {
          console.error('Failed to parse initial blocks:', error);
          setBlocks(undefined);
        }
      } else {
        setBlocks(undefined);
      }
      setSearchValue("");
      setShowSearchDropdown(false);
    }
  }, [open, initialTitle, initialAssociations, initialBlocks]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };

    if (showSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSearchDropdown]);

  const searchContacts = searchData?.contacts?.edges?.map(e => e?.node).filter(Boolean) || [];
  const searchAccounts = searchData?.accounts?.edges?.map(e => e?.node).filter(Boolean) || [];
  const searchDeals = searchData?.deals?.edges?.map(e => e?.node).filter(Boolean) || [];
  const hasBackendResults = searchContacts.length > 0 || searchAccounts.length > 0 || searchDeals.length > 0;

  // Fallback to dummy data when backend returns no results (e.g., preview mode)
  const dummyResults = useMemo(() => {
    if (hasBackendResults || !searchValue.trim()) return { contacts: [], accounts: [], deals: [] };
    const q = searchValue.toLowerCase();
    const selectedIds = new Set(subjects.map(s => s.id));
    return {
      contacts: people.filter(p => p.name.toLowerCase().includes(q) && !selectedIds.has(p.id)).slice(0, 5),
      accounts: companies.filter(c => c.name.toLowerCase().includes(q) && !selectedIds.has(c.id)).slice(0, 5),
      deals: deals.filter(d => d.name.toLowerCase().includes(q) && !selectedIds.has(d.id)).slice(0, 5),
    };
  }, [searchValue, hasBackendResults, subjects]);

  const hasDummyResults = dummyResults.contacts.length > 0 || dummyResults.accounts.length > 0 || dummyResults.deals.length > 0;
  const hasSearchResults = hasBackendResults || hasDummyResults;

  const handleSubjectSelected = useCallback(
    (subject: NoteSubject) => {
      // If the subject is already selected, don't add it again
      if (subjects?.find((_) => _.id === subject.id)) {
        return;
      }

      let newSubjects = [...subjects, subject];

      // If the selected subject is a contact or deal, automatically add its associated accounts
      if (subject.__typename === "Contact" || subject.__typename === "Deal") {
        const entity = subject as Contact | Deal;
        const associatedAccounts =
          entity.accounts?.edges
            ?.map((edge) => edge?.node)
            .filter((account): account is Account => !!account) || [];

        // Add associated accounts that aren't already selected
        associatedAccounts.forEach((account) => {
          if (!newSubjects.find((_) => _.id === account.id)) {
            newSubjects.push(account);
          }
        });
      }

      setSubjects(newSubjects);
    },
    [subjects]
  );

  const handleRemoveSubject = (id: string) => {
    setSubjects(prev => prev.filter(s => s.id !== id));
  };

  const reset = () => {
    setTitle("");
    setBlocks(undefined);
    setSubjects([]);
  };

  const isBlocksEmpty = (blocks?: PartialBlock[]) => {
    if (!blocks || blocks.length === 0) return true;
    // Check if all blocks are empty paragraphs
    return blocks.every(block =>
      !block.content ||
      (Array.isArray(block.content) && block.content.length === 0)
    );
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error(t("forms.note.missingTitle"));
      return;
    }

    if (isBlocksEmpty(blocks)) {
      toast.error(t("forms.note.missingContent"));
      return;
    }

    // Only require associations if no engagement context
    if (!contextEngagementId && (!subjects || subjects.length === 0)) {
      toast.error(t("forms.note.missingAssociation"));
      return;
    }

    if (!workspaceId) {
      toast.error(t("forms.note.noWorkspace"));
      return;
    }

    const accountIds = subjects?.filter((_) => _.__typename == "Account").map((_) => _.id);
    const contactIds = subjects?.filter((_) => _.__typename == "Contact").map((_) => _.id);
    const dealIds = subjects?.filter((_) => _.__typename == "Deal").map((_) => _.id);
    const engagementIds = contextEngagementId ? [contextEngagementId] : [];

    try {
      if (isEditMode && noteId) {
        // Update existing note
        await updateNoteMutation({
          variables: {
            input: {
              id: noteId,
              workspaceId: String(workspaceId),
              title: title.trim(),
              blocks: JSON.stringify(blocks || []),
              accountIds,
              contactIds,
              dealIds,
              engagementIds,
            },
          },
        });
        toast.success(t("forms.note.updated"));
      } else {
        // In preview mode, persist locally so the note shows up in the list.
        if (isLovablePreview()) {
          try {
            const accountSubjects = subjects?.filter((_) => _.__typename === "Account") as any[];
            const contactSubjects = subjects?.filter((_) => _.__typename === "Contact") as any[];
            const companyName = accountSubjects?.[0]?.properties?.name || accountSubjects?.[0]?.domain || "";
            const peopleNames = (contactSubjects || []).map((c: any) => {
              const props = c?.properties;
              const full = [props?.firstName, props?.lastName].filter(Boolean).join(" ");
              return full || props?.email || "";
            }).filter(Boolean);
            const previewNote = {
              id: `preview-${Date.now()}`,
              name: title.trim(),
              company: companyName,
              people: peopleNames,
              // Preview-only display string; goes through the shared locale-aware
              // formatter rather than a hardcoded "en-US".
              date: formatDate(new Date()),
              visibility: "Private",
            };
            const raw = localStorage.getItem("preview-created-notes");
            const list = raw ? JSON.parse(raw) : [];
            list.unshift(previewNote);
            localStorage.setItem("preview-created-notes", JSON.stringify(list));
            window.dispatchEvent(new CustomEvent("preview-notes-updated"));
          } catch (e) {
            console.warn("Failed to persist preview note", e);
          }
        } else {
          // Create new note
          const result = await createNoteMutation({
            variables: {
              input: {
                workspaceId: String(workspaceId),
                userId: currentUser?.id ?? "",
                accountIds,
                contactIds,
                dealIds,
                engagementIds,
                title: title.trim(),
                blocks: JSON.stringify(blocks || []),
                type: NoteTypeEnum.Note,
              },
            },
          });
          // Refresh the mounted note lists (Notes page, detail-sheet Notes tabs)
          // until the search index returns the new note, in the background so the
          // dialog closes immediately. A single refetch here used to land before
          // the index had the note and cached an empty list until a page reload.
          // Create-only: updates flow through the normalized cache (see the
          // CLAUDE.md rule on refetchQueries for update mutations).
          settleCreatedNote(result.data?.createNote?.note?.id);
        }
        toast.success(t("forms.note.created"));
      }

      reset();
      onOpenChange(false);
    } catch (err) {
      const errorMsg = err instanceof Error
        ? err.message
        : t(isEditMode ? "forms.note.updateFailed" : "forms.note.createFailed");
      toast.error(errorMsg);
    }
  };

  const handleCancel = () => {
    reset();
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) handleCancel(); else onOpenChange(v); }}>
      <SheetContent expandable className="sm:max-w-[50vw] w-full overflow-y-auto p-0 flex flex-col" onOpenAutoFocus={(e) => e.preventDefault()}>
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
          <SheetTitle className="text-lg font-display">{isEditMode ? t("forms.note.editTitle") : t("dialogs.addNote")}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="note-title" className="text-sm">{t("common.title")}</Label>
            <Input
              id="note-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder={t("forms.note.titlePlaceholder")}
              autoFocus
            />
          </div>

          {/* Content Editor */}
          <div className="space-y-1.5 flex-1 min-h-0">
            <Label className="text-sm">{t("forms.note.content")}</Label>
            <div className="border border-border rounded-lg bg-card">
              <NoteEditor
                key={noteId || 'new-note'}
                initialContent={blocks}
                onChange={(newBlocks) => setBlocks(newBlocks)}
                autoFocus={false}
              />
            </div>
          </div>

          {/* Associate With - Subject Pills and Search */}
          <div className="space-y-1.5">
            <Label className="text-sm">{t("forms.labels.associateWith")}</Label>

            {/* Subject Pills */}
            {subjects.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {subjects.map((subject) => (
                  <SubjectPill
                    key={subject.id}
                    subject={subject}
                    onRemove={() => handleRemoveSubject(subject.id)}
                  />
                ))}
              </div>
            )}

            {/* Inline Search */}
            <div className="relative" ref={dropdownRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchValue}
                  onChange={e => {
                    setSearchValue(e.target.value);
                    setShowSearchDropdown(true);
                  }}
                  onFocus={() => setShowSearchDropdown(true)}
                  placeholder={t("forms.labels.searchSubjects")}
                  className="pl-9"
                />
                {searchValue && (
                  <button
                    onClick={() => {
                      setSearchValue("");
                      setShowSearchDropdown(false);
                    }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Search Dropdown */}
              {showSearchDropdown && searchValue && (
                <div className="absolute z-10 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                  {searchLoading && (
                    <div className="text-center py-8 text-muted-foreground text-xs">{t("forms.actions.searching")}</div>
                  )}

                  {!searchLoading && !hasSearchResults && (
                    <div className="text-center py-8 text-muted-foreground text-xs">{t("common.noResults")}</div>
                  )}

                  {!searchLoading && hasSearchResults && (
                    <div className="p-2">
                      {/* Backend results */}
                      {searchContacts.map((contact) => (
                        <button
                          key={contact.id}
                          onClick={() => {
                            handleSubjectSelected(contact as Contact);
                            setSearchValue("");
                            setShowSearchDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          <UserAvatar
                            name={`${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim()}
                            src={contact.profileImageUrl}
                            size="sm"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {`${contact.properties?.firstName || ''} ${contact.properties?.lastName || ''}`.trim() || contact.primaryEmail}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {contact.accounts?.edges?.[0]?.node?.properties?.name || contact.accounts?.edges?.[0]?.node?.domain || ''}
                            </div>
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("forms.subjectType.person")}</span>
                        </button>
                      ))}

                      {searchAccounts.map((account) => (
                        <button
                          key={account.id}
                          onClick={() => {
                            handleSubjectSelected(account as Account);
                            setSearchValue("");
                            setShowSearchDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          <CompanyLogo
                            name={account.properties?.name || account.domain || ''}
                            domain={account.domain}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {account.properties?.name || account.domain}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {account.domain}
                            </div>
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("forms.subjectType.company")}</span>
                        </button>
                      ))}

                      {searchDeals.map((deal) => (
                        <button
                          key={deal.id}
                          onClick={() => {
                            handleSubjectSelected(deal as Deal);
                            setSearchValue("");
                            setShowSearchDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                            <Handshake className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">
                              {deal.properties?.name || t("forms.labels.unnamedDeal")}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {deal.accounts?.edges?.[0]?.node?.properties?.name || ''}
                            </div>
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("forms.subjectType.deal")}</span>
                        </button>
                      ))}

                      {/* Dummy data fallback results */}
                      {!hasBackendResults && dummyResults.contacts.map((p) => (
                        <button
                          key={`dummy-person-${p.id}`}
                          onClick={() => {
                            handleSubjectSelected({ __typename: "Contact", id: p.id, properties: { firstName: p.name.split(' ')[0], lastName: p.name.split(' ').slice(1).join(' ') } } as any);
                            setSearchValue("");
                            setShowSearchDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          <UserAvatar name={p.name} size="sm" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{p.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{p.company}</div>
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("forms.subjectType.person")}</span>
                        </button>
                      ))}

                      {!hasBackendResults && dummyResults.accounts.map((c) => (
                        <button
                          key={`dummy-company-${c.id}`}
                          onClick={() => {
                            handleSubjectSelected({ __typename: "Account", id: c.id, properties: { name: c.name }, domain: c.website } as any);
                            setSearchValue("");
                            setShowSearchDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          <CompanyLogo name={c.name} domain={c.website} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{c.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{c.website}</div>
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("forms.subjectType.company")}</span>
                        </button>
                      ))}

                      {!hasBackendResults && dummyResults.deals.map((d) => (
                        <button
                          key={`dummy-deal-${d.id}`}
                          onClick={() => {
                            handleSubjectSelected({ __typename: "Deal", id: d.id, properties: { name: d.name } } as any);
                            setSearchValue("");
                            setShowSearchDropdown(false);
                          }}
                          className="w-full flex items-center gap-3 p-2 rounded-md hover:bg-muted text-left"
                        >
                          <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                            <Handshake className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{d.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{d.company}</div>
                          </div>
                          <span className="text-[10px] uppercase text-muted-foreground tracking-wider">{t("forms.subjectType.deal")}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border px-6 py-4 flex justify-end gap-2">
          <Button variant="outline" onClick={handleCancel}>{t("common.cancel")}</Button>
          <Button onClick={handleSubmit} disabled={!title.trim() || isBlocksEmpty(blocks) || (!contextEngagementId && !subjects.length) || creatingNote || updatingNote}>
            {creatingNote || updatingNote ? t("forms.actions.saving") : isEditMode ? t("forms.note.update") : t("forms.note.save")}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Subject Pill Component
function SubjectPill({ subject, onRemove }: { subject: NoteSubject; onRemove: () => void }) {
  const { t } = useTranslation();
  const getName = () => {
    if (subject.__typename === "Contact") {
      return getDisplayName(subject.properties?.firstName, subject.properties?.lastName, subject.primaryEmail);
    }
    if (subject.__typename === "Account") {
      return subject.properties?.name || subject.domain || t("forms.labels.unknownCompany");
    }
    if (subject.__typename === "Deal") {
      return subject.properties?.name || t("forms.labels.unnamedDeal");
    }
    return '';
  };

  const getAvatar = () => {
    if (subject.__typename === "Contact") {
      return (
        <UserAvatar
          name={getName()}
          src={subject.profileImageUrl}
          size="xs"
        />
      );
    }
    if (subject.__typename === "Account") {
      return (
        <CompanyLogo
          name={getName()}
          domain={subject.domain}
        />
      );
    }
    return <Handshake className="h-4 w-4 text-muted-foreground" />;
  };

  return (
    <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-card text-xs">
      {getAvatar()}
      <span>{getName()}</span>
      <button
        onClick={onRemove}
        className="ml-0.5 hover:text-foreground text-muted-foreground"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
